import type { Schema } from 'effect';

import type { OrderSummaryAdapters, PaymentRequestAdapters } from './orderAdapters';
import { makeAddProductVariantToOrder } from '../../request/backendApi/order/makeAddProductVariantToOrder';
import { makeCompleteOrder } from '../../request/backendApi/order/makeCompleteOrder';
import { makeCreateOrder } from '../../request/backendApi/order/makeCreateOrder';
import { makeCreatePaymentRequestForOrder } from '../../request/backendApi/order/makeCreatePaymentRequestForOrder';
import { makeEditProductVariantQuantityInOrder } from '../../request/backendApi/order/makeEditProductVariantQuantityInOrder';
import { makeGetOrderByIdentifierExpanded } from '../../request/backendApi/order/makeGetOrderByIdentifierExpanded';
import { makeGetOrderExpanded } from '../../request/backendApi/order/makeGetOrderExpanded';
import { makeGetPaymentRequestByHashCheck } from '../../request/backendApi/order/makeGetPaymentRequestByHashCheck';
import { makeGetPaymentRequestWithOrder } from '../../request/backendApi/order/makeGetPaymentRequestWithOrder';
import { makeListOrdersForUser } from '../../request/backendApi/order/makeListOrdersForUser';
import { makeListShippingAndPaymentsForOrder } from '../../request/backendApi/order/makeListShippingAndPaymentsForOrder';
import { makeUpdateOrder } from '../../request/backendApi/order/makeUpdateOrder';
import { makeUpdatePaymentInOrder } from '../../request/backendApi/order/makeUpdatePaymentInOrder';
import { makeUpdateShippingInOrder } from '../../request/backendApi/order/makeUpdateShippingInOrder';
import type { StripJsonLd } from '@vs-shop/schemas/transform/withoutJsonLd';

/**
 * Seam-C (workspace#49 option C — layer 2: per-domain module factory, request cluster).
 *
 * Engine-side order request family. Per-request factories retain the original
 * endpoint, error-family, span, log, and response-decoding behaviour. This
 * assembler preserves the tenant-facing factory contract while passing every
 * factory only the domain dependencies it uses.
 */
export type OrderRequestsDeps<
  OShowType,
  PRType,
  ODetail extends Schema.Top,
  ODetailPaid extends Schema.Top,
  OIndex extends Schema.Top,
  ShipOpts extends Schema.Top,
> = {
  /** Cart-show mutation response (`OrderShow`); its Type feeds the summary adapters. */
  readonly orderShow: Schema.Codec<OShowType, unknown>;
  readonly orderDetail: ODetail;
  readonly orderDetailPaid: ODetailPaid;
  readonly orderIndex: OIndex;
  readonly shippingOptions: ShipOpts;
  /** Payment-request response (`PaymentRequestExtended`); its Type feeds the PR adapters. */
  readonly paymentRequestExtended: Schema.Codec<PRType, unknown>;
  // `schemaBodyJsonClean` strips JSON-LD metadata, so the decoded value the
  // engine hands to these projections is `StripJsonLd<…>`, not the raw Type.
  readonly orderSummaryAdapters: OrderSummaryAdapters<StripJsonLd<OShowType>>;
  readonly paymentRequestAdapters: PaymentRequestAdapters<StripJsonLd<PRType>>;
};

export const makeOrderRequests = <
  OShowType,
  PRType,
  ODetail extends Schema.Top,
  ODetailPaid extends Schema.Top,
  OIndex extends Schema.Top,
  ShipOpts extends Schema.Top,
>(
  deps: OrderRequestsDeps<OShowType, PRType, ODetail, ODetailPaid, OIndex, ShipOpts>
) => {
  const addProductVariantToOrder = makeAddProductVariantToOrder({
    orderShow: deps.orderShow,
  });
  const completeOrder = makeCompleteOrder({
    orderShow: deps.orderShow,
    orderSummaryAdapters: deps.orderSummaryAdapters,
  });
  const createOrder = makeCreateOrder({
    orderShow: deps.orderShow,
    orderSummaryAdapters: deps.orderSummaryAdapters,
  });
  const createPaymentRequestForOrder = makeCreatePaymentRequestForOrder({
    paymentRequestExtended: deps.paymentRequestExtended,
    paymentRequestAdapters: deps.paymentRequestAdapters,
  });
  const editProductVariantQuantityInOrder = makeEditProductVariantQuantityInOrder({
    orderShow: deps.orderShow,
  });
  const getOrderByIdentifierExpanded = makeGetOrderByIdentifierExpanded({
    orderDetailPaid: deps.orderDetailPaid,
  });
  const getOrderExpanded = makeGetOrderExpanded({
    orderDetail: deps.orderDetail,
  });
  const listOrdersForUser = makeListOrdersForUser({
    orderIndex: deps.orderIndex,
  });
  const getPaymentRequestByHashCheck = makeGetPaymentRequestByHashCheck({
    paymentRequestExtended: deps.paymentRequestExtended,
    paymentRequestAdapters: deps.paymentRequestAdapters,
  });
  const getPaymentRequestWithOrder = makeGetPaymentRequestWithOrder({
    getPaymentRequestByHashCheck: getPaymentRequestByHashCheck.getPaymentRequestByHashCheck,
    getOrderExpanded: getOrderExpanded.getOrderExpanded,
    paymentRequestAdapters: deps.paymentRequestAdapters,
  });
  const listShippingAndPaymentsForOrder = makeListShippingAndPaymentsForOrder({
    shippingOptions: deps.shippingOptions,
  });
  const updateOrder = makeUpdateOrder({
    orderShow: deps.orderShow,
  });
  const updatePaymentInOrder = makeUpdatePaymentInOrder({
    orderShow: deps.orderShow,
  });
  const updateShippingInOrder = makeUpdateShippingInOrder({
    orderShow: deps.orderShow,
  });

  return {
    ...addProductVariantToOrder,
    ...completeOrder,
    ...createOrder,
    ...createPaymentRequestForOrder,
    ...editProductVariantQuantityInOrder,
    ...getOrderByIdentifierExpanded,
    ...getOrderExpanded,
    ...listOrdersForUser,
    ...getPaymentRequestByHashCheck,
    ...getPaymentRequestWithOrder,
    ...listShippingAndPaymentsForOrder,
    ...updateOrder,
    ...updatePaymentInOrder,
    ...updateShippingInOrder,
  };
};

export type { CreatePaymentRequestPayload } from '../../request/backendApi/order/makeCreatePaymentRequestForOrder';
